import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider, Navigate, useParams } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { client } from './lib/apollo-client';

// Initialize Sentry before anything else
import { initSentry } from './lib/sentry';
initSentry();

import { installStaleChunkReload } from './lib/staleChunkReload';
installStaleChunkReload();

// Initialize i18n (must be imported before App)
import './i18n';
import App from './App.tsx';
import Projects from './pages/Projects.tsx';
import Library from './pages/Library.tsx';
import Clips from './pages/Clips.tsx';
import PublishCalendar from './pages/PublishCalendar.tsx';
import PublishQueuesPage from './pages/PublishQueuesPage.tsx';
import ProjectClips from './pages/ProjectClips.tsx';
import LibraryCopilotPage from './pages/LibraryCopilotPage.tsx';
import ProjectCopilotPage from './pages/ProjectCopilotPage.tsx';
import LibraryClips from './pages/LibraryClips.tsx';
import PricingPlans from './pages/PricingPlans.tsx';
import PlanCheckout from './pages/PlanCheckout.tsx';
import Tutorials from './pages/Tutorials.tsx';
import CoursePage from './pages/CoursePage.tsx';
import CourseModulePage from './pages/CourseModulePage.tsx';
import CourseCheckout from './pages/CourseCheckout.tsx';
import ApiKeys from './pages/ApiKeys.tsx';
import ApiRequests from './pages/ApiRequests.tsx';
import ApiDocumentation from './pages/ApiDocumentation.tsx';
import ApiGettingStarted from './pages/ApiGettingStarted.tsx';
import ApiReference from './pages/ApiReference.tsx';
import ApiPlayground from './pages/ApiPlayground.tsx';
import NotFound from './pages/NotFound.tsx';
import Login from './pages/Login.tsx';
import LoginMicrosoft from './pages/LoginMicrosoft.tsx';
import LoginMicrosoftCallback from './pages/LoginMicrosoftCallback.tsx';
import LoginGoogleCallback from './pages/LoginGoogleCallback.tsx';
import SocialOauthRelay from './pages/SocialOauthRelay.tsx';
import ClipMcpResume from './pages/ClipMcpResume.tsx';
import ConnectAi from './pages/ConnectAi.tsx';
import Register from './pages/Register.tsx';
import ForgotPassword from './pages/ForgotPassword.tsx';
import ResetPassword from './pages/ResetPassword.tsx';
import ClipEdit from './pages/ClipEdit.tsx';
import ClipStudio from './pages/ClipStudio.tsx';
import FreecutStudioFeatureRoute from './components/FreecutStudioFeatureRoute.tsx';
import RedirectLegacyClipStudio from './components/RedirectLegacyClipStudio.tsx';
import QuoteCarouselPage from './pages/QuoteCarouselPage.tsx';
import Challenge from './pages/Challenge.tsx';
import CampaignDetail from './pages/CampaignDetail.tsx';
import SuperHeroEntry from './pages/SuperHeroEntry.tsx';
import EarnPage from './pages/EarnPage.tsx';
import EarnWallet from './pages/EarnWallet.tsx';
import ClipperRanksPage from './pages/ClipperRanksPage.tsx';
import CourseRoyaltiesPage from './pages/CourseRoyaltiesPage.tsx';
import AnalyticsPage from './pages/AnalyticsPage.tsx';
import AffiliatesPage from './pages/AffiliatesPage.tsx';
import InvitationAccept from './pages/InvitationAccept.tsx';
import LinkAccount from './pages/LinkAccount.tsx';
import Invoice from './pages/Invoice.tsx';
import BrandKits from './pages/BrandKits.tsx';
import BrandKitsNew from './pages/BrandKitsNew.tsx';
import MyMediaPage from './pages/MyMediaPage.tsx';
import FontPreview from './pages/FontPreview.tsx';
import SharePage from './pages/SharePage.tsx';
import ShortLinkPage from './pages/ShortLinkPage.tsx';
import TermsOfUse from './pages/TermsOfUse.tsx';
import PrivacyPolicy from './pages/PrivacyPolicy.tsx';
import FacebookDataDeletion from './pages/FacebookDataDeletion.tsx';
import PlansList from './pages/PlansList.tsx';
import PurchaseComplete from './pages/PurchaseComplete.tsx';
import ProtectedRoute from './components/ProtectedRoute.tsx';
import EmbeddedPlansGuard from './components/EmbeddedPlansGuard.tsx';
import ProOnlyRoute from './components/ProOnlyRoute.tsx';
import AdminRoute from './components/AdminRoute.tsx';
import SocialPublishingRoute from './components/SocialPublishingRoute.tsx';
import QuoteCarouselFeatureRoute from './components/QuoteCarouselFeatureRoute.tsx';
import ObjectiveRoute from './components/ObjectiveRoute.tsx';
import AdminUsers from './pages/admin/AdminUsers.tsx';
import AdminProjects from './pages/admin/AdminProjects.tsx';
import AdminClips from './pages/admin/AdminClips.tsx';
import AdminDownloads from './pages/admin/AdminDownloads.tsx';
import AdminCoupons from './pages/admin/AdminCoupons.tsx';
import AdminLiveWatches from './pages/admin/AdminLiveWatches.tsx';
import AdminLiveWatchDetail from './pages/admin/AdminLiveWatchDetail.tsx';
import AdminCourseRevenueShares from './pages/admin/AdminCourseRevenueShares.tsx';
import AdminAffiliateEarnings from './pages/admin/AdminAffiliateEarnings.tsx';
import AdminEmailFlows from './pages/admin/AdminEmailFlows.tsx';
import AdminEmailFlowDetail from './pages/admin/AdminEmailFlowDetail.tsx';
import AdminPayouts from './pages/admin/AdminPayouts.tsx';
import AdminPixVerifications from './pages/admin/AdminPixVerifications.tsx';
import AdminContatos from './pages/admin/AdminContatos.tsx';
import AdminCpmLeads from './pages/admin/AdminCpmLeads.tsx';
import AdminChallenges from './pages/admin/AdminChallenges.tsx';
import AdminChallengeParticipants from './pages/admin/AdminChallengeParticipants.tsx';
import AdminChallengeClips from './pages/admin/AdminChallengeClips.tsx';
import AdminChallengeAudit from './pages/admin/AdminChallengeAudit.tsx';
import AdminChallengeRankings from './pages/admin/AdminChallengeRankings.tsx';
import AdminLibrary from './pages/admin/AdminLibrary.tsx';
import AdminBrandKitNew from './pages/admin/AdminBrandKitNew.tsx';
import AdminTemplateSuggestions from './pages/admin/AdminTemplateSuggestions.tsx';
import AdminCommunityCuration from './pages/admin/AdminCommunityCuration.tsx';
import { AiStudioRoute } from './components/assistant/AiStudioRoute.tsx';
import AdminAiUsage from './pages/admin/AdminAiUsage.tsx';
import { MotionTest } from './pages/admin/MotionTest.tsx';
import AdminCampaignsListPage from './pages/admin/AdminCampaignsListPage.tsx';
import AdminClipperSeasons from './pages/admin/AdminClipperSeasons.tsx';
import AdminCampaignEditPage from './pages/admin/AdminCampaignEditPage.tsx';
import AdminCampaignSubmissionsPage from './pages/admin/AdminCampaignSubmissionsPage.tsx';
import AdminCampaignParticipantsPage from './pages/admin/AdminCampaignParticipantsPage.tsx';
import AdminChannels from './pages/admin/AdminChannels.tsx';
import AdminResultados from './pages/admin/AdminResultados.tsx';
import CampaignAuditListPage from './pages/CampaignAuditListPage.tsx';
import CommunityLibrary from './pages/CommunityLibrary.tsx';
import TemplateIdeas from './pages/TemplateIdeas.tsx';
import HireClippersPage from './pages/HireClippersPage.tsx';
import { HIRE_CLIPPERS_PATH } from './lib/brandLeads/hireClippersRoute';
import AdminUserDetail from './pages/admin/AdminUserDetail.tsx';
import AdminUserBotConversations from './pages/admin/AdminUserBotConversations.tsx';
import AdminBotConversations from './pages/admin/AdminBotConversations.tsx';
import AdminBotConversationDetail from './pages/admin/AdminBotConversationDetail.tsx';
import LibraryDebug from './pages/LibraryDebug.tsx';
import TransitionsTestPage from './pages/TransitionsTestPage.tsx';
import { Sso } from './pages/Sso.tsx';
import { useHomeRoute } from './hooks/use-home-route';
import { isClipMcpConnectPageEnabled } from './lib/clipMcpFlag';
import { TEMPLATE_NEW_PATH, TEMPLATES_PATH, templateEditPath } from './lib/brand-kit/templateRoutes';
import './index.css';

const HomeRedirect = () => {
  const { route: homeRoute, loading } = useHomeRoute();
  if (loading) return null;
  return <Navigate to={homeRoute} replace />;
};

const RedirectBrandKitToTemplate = () => {
  const { id } = useParams();
  return <Navigate to={id ? templateEditPath(id) : TEMPLATES_PATH} replace />;
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      { index: true, element: <HomeRedirect /> },
      {
        path: '/projects',
        element: (
          <ProtectedRoute>
            <ObjectiveRoute for="own_brand">
              <Projects />
            </ObjectiveRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/library',
        element: (
          <ProtectedRoute>
            <ObjectiveRoute for="other_channels">
              <Library />
            </ObjectiveRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/community-library',
        element: (
          <ProtectedRoute>
            <CommunityLibrary />
          </ProtectedRoute>
        ),
      },
      {
        path: '/template-ideas',
        element: (
          <ProtectedRoute>
            <TemplateIdeas />
          </ProtectedRoute>
        ),
      },
      {
        path: HIRE_CLIPPERS_PATH,
        element: (
          <ProtectedRoute>
            <HireClippersPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/clips',
        element: (
          <ProtectedRoute>
            <Clips />
          </ProtectedRoute>
        ),
      },
      {
        path: '/calendar',
        element: (
          <ProtectedRoute>
            <SocialPublishingRoute>
              <PublishCalendar />
            </SocialPublishingRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/queues',
        element: (
          <ProtectedRoute>
            <SocialPublishingRoute>
              <PublishQueuesPage />
            </SocialPublishingRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/plans',
        element: (
          <ProtectedRoute>
            <EmbeddedPlansGuard>
              <PricingPlans />
            </EmbeddedPlansGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: '/plans/:planId',
        element: (
          <ProtectedRoute>
            <EmbeddedPlansGuard>
              <PlanCheckout />
            </EmbeddedPlansGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: '/tutorials',
        element: (
          <ProtectedRoute>
            <Tutorials />
          </ProtectedRoute>
        ),
      },
      {
        path: '/courses/:slug',
        element: (
          <ProtectedRoute>
            <CoursePage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/courses/:slug/checkout',
        element: (
          <ProtectedRoute>
            <CourseCheckout />
          </ProtectedRoute>
        ),
      },
      {
        path: '/courses/:slug/:moduleOrder',
        element: (
          <ProtectedRoute>
            <CourseModulePage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/api-keys',
        element: (
          <ProOnlyRoute>
            <ApiKeys />
          </ProOnlyRoute>
        ),
      },
      {
        path: '/api-requests',
        element: (
          <ProOnlyRoute>
            <ApiRequests />
          </ProOnlyRoute>
        ),
      },
      {
        path: '/api-documentation',
        element: (
          <ProOnlyRoute>
            <ApiDocumentation />
          </ProOnlyRoute>
        ),
      },
      {
        path: '/api-getting-started',
        element: (
          <ProOnlyRoute>
            <ApiGettingStarted />
          </ProOnlyRoute>
        ),
      },
      {
        path: '/api-reference',
        element: (
          <ProOnlyRoute>
            <ApiReference />
          </ProOnlyRoute>
        ),
      },
      {
        path: '/api-playground',
        element: (
          <ProOnlyRoute>
            <ApiPlayground />
          </ProOnlyRoute>
        ),
      },
      {
        path: TEMPLATES_PATH,
        element: (
          <ProOnlyRoute>
            <BrandKits />
          </ProOnlyRoute>
        ),
      },
      {
        path: TEMPLATE_NEW_PATH,
        element: (
          <ProOnlyRoute>
            <BrandKitsNew />
          </ProOnlyRoute>
        ),
      },
      {
        path: '/templates/:id',
        element: (
          <ProOnlyRoute>
            <BrandKitsNew />
          </ProOnlyRoute>
        ),
      },
      {
        path: '/brand-kits',
        element: <Navigate to={TEMPLATES_PATH} replace />,
      },
      {
        path: '/brand-kits-new',
        element: <Navigate to={TEMPLATE_NEW_PATH} replace />,
      },
      {
        path: '/brand-kits/:id',
        element: <RedirectBrandKitToTemplate />,
      },
      {
        path: '/branding',
        element: <Navigate to={TEMPLATES_PATH} replace />,
      },
      {
        path: '/my-media',
        element: (
          <ProtectedRoute>
            <MyMediaPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/historic',
        element: <HomeRedirect />,
      },
      {
        path: '/project/:projectId/clips',
        element: (
          <ProtectedRoute>
            <ObjectiveRoute for="own_brand">
              <ProjectClips />
            </ObjectiveRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/project/:projectId/edit',
        element: (
          <ProtectedRoute>
            <ObjectiveRoute for="own_brand">
              <ProjectCopilotPage />
            </ObjectiveRoute>
          </ProtectedRoute>
        ),
      },
      {
        // AI Studio: the same editor, preview + assistant chat only.
        path: '/project/:projectId/ai-studio',
        element: (
          <ProtectedRoute>
            <ObjectiveRoute for="own_brand">
              <AiStudioRoute>
                <ProjectCopilotPage variant="studio" />
              </AiStudioRoute>
            </ObjectiveRoute>
          </ProtectedRoute>
        ),
      },
      {
        // No ObjectiveRoute here: own_brand users reach a remixed/uploaded
        // library through Acervo (not the /library list) and must be able to
        // open its editor. /library (the list) below stays clipper-gated.
        path: '/library/:id/edit',
        element: (
          <ProtectedRoute>
            <LibraryCopilotPage />
          </ProtectedRoute>
        ),
      },
      {
        // AI Studio for libraries; reachable like /library/:id/edit.
        path: '/library/:id/ai-studio',
        element: (
          <ProtectedRoute>
            <AiStudioRoute>
              <LibraryCopilotPage variant="studio" />
            </AiStudioRoute>
          </ProtectedRoute>
        ),
      },
      {
        // Same as /library/:id/edit above — reachable from Acervo regardless
        // of objective; only the /library list stays other_channels-only.
        path: '/library/:libraryId/clips',
        element: (
          <ProtectedRoute>
            <LibraryClips />
          </ProtectedRoute>
        ),
      },
      {
        path: '/library/:libraryId/debug',
        element: (
          <AdminRoute>
            <LibraryDebug />
          </AdminRoute>
        ),
      },
      {
        path: '/clips/:clipId/carousel',
        element: (
          <ProtectedRoute>
            <QuoteCarouselFeatureRoute>
              <QuoteCarouselPage />
            </QuoteCarouselFeatureRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/clips/:id/edit',
        element: (
          <ProOnlyRoute>
            <ClipEdit />
          </ProOnlyRoute>
        ),
      },
      {
        path: '/clips/:id/autocut',
        element: (
          <ProOnlyRoute allowAdmins>
            <FreecutStudioFeatureRoute>
              <ClipStudio />
            </FreecutStudioFeatureRoute>
          </ProOnlyRoute>
        ),
      },
      {
        path: '/clips/:id/freecut',
        element: <RedirectLegacyClipStudio />,
      },
      {
        path: '/invitation/:token',
        element: (
          <ProtectedRoute>
            <InvitationAccept />
          </ProtectedRoute>
        ),
      },
      {
        path: '/link/:code',
        element: (
          <ProtectedRoute>
            <LinkAccount />
          </ProtectedRoute>
        ),
      },
      {
        path: '/invoice',
        element: (
          <ProtectedRoute>
            <Invoice />
          </ProtectedRoute>
        ),
      },
      {
        path: '/challenge/:id',
        element: (
          <ProtectedRoute>
            <Challenge />
          </ProtectedRoute>
        ),
      },
      {
        path: '/campaigns/:id',
        element: (
          <ProtectedRoute>
            <CampaignDetail />
          </ProtectedRoute>
        ),
      },
      {
        path: '/earn',
        element: (
          <ProtectedRoute>
            <EarnPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/rankings',
        element: (
          <ProtectedRoute>
            <ObjectiveRoute for="other_channels">
              <ClipperRanksPage />
            </ObjectiveRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/ranks',
        element: <Navigate to="/rankings" replace />,
      },
      {
        path: '/earn/wallet',
        element: (
          <ProtectedRoute>
            <EarnWallet />
          </ProtectedRoute>
        ),
      },
      {
        path: '/analytics',
        element: (
          <ProtectedRoute>
            <AnalyticsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/course-royalties',
        element: (
          <ProtectedRoute>
            <CourseRoyaltiesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/affiliates',
        element: (
          <ProtectedRoute>
            <AffiliatesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/login',
        element: <Login />,
      },
      {
        path: '/super-hero',
        element: <SuperHeroEntry />,
      },
      {
        path: '/login/microsoft',
        element: <LoginMicrosoft />,
      },
      {
        path: '/login/microsoft/callback',
        element: <LoginMicrosoftCallback />,
      },
      {
        path: '/login/google/callback',
        element: <LoginGoogleCallback />,
      },
      {
        path: '/social-oauth',
        element: (
          <ProtectedRoute>
            <SocialOauthRelay />
          </ProtectedRoute>
        ),
      },
      {
        path: '/clip-mcp/resume',
        element: (
          <ProtectedRoute>
            <ClipMcpResume />
          </ProtectedRoute>
        ),
      },
      {
        path: '/connect-ai',
        element: isClipMcpConnectPageEnabled() ? (
          <ProtectedRoute>
            <ConnectAi />
          </ProtectedRoute>
        ) : (
          <NotFound />
        ),
      },
      {
        path: '/register',
        element: <Register />,
      },
      {
        path: '/forgot-password',
        element: <ForgotPassword />,
      },
      {
        path: '/reset-password',
        element: <ResetPassword />,
      },
      {
        path: '/sso',
        element: <Sso />,
      },
      {
        path: '/share',
        element: <SharePage />,
      },
      {
        path: '/s/:code',
        element: <ShortLinkPage />,
      },
      {
        path: '/font-preview',
        element: <FontPreview />,
      },
      {
        path: '/transitions-test',
        element: <TransitionsTestPage />,
      },
      {
        path: '/terms',
        element: <TermsOfUse />,
      },
      {
        path: '/facebook-data-deletion',
        element: <FacebookDataDeletion />,
      },
      {
        path: '/privacy',
        element: <PrivacyPolicy />,
      },
      {
        // Legacy combined document. Kept so old links (register e-mails, the
        // marketing site, app-store listings) keep resolving.
        path: '/terms-and-privacy',
        element: <Navigate to="/terms" replace />,
      },
      {
        path: '/plans-list',
        element: <PlansList />,
      },
      {
        path: '/purchase-complete',
        element: (
          <ProtectedRoute>
            <PurchaseComplete />
          </ProtectedRoute>
        ),
      },
      // Admin routes
      {
        path: '/admin/resultados',
        element: (
          <AdminRoute>
            <AdminResultados />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/users',
        element: (
          <AdminRoute>
            <AdminUsers />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/users/:id',
        element: (
          <AdminRoute>
            <AdminUserDetail />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/users/:id/conversations',
        element: (
          <AdminRoute>
            <AdminUserBotConversations />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/bot-conversations',
        element: (
          <AdminRoute>
            <AdminBotConversations />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/bot-conversations/:id',
        element: (
          <AdminRoute>
            <AdminBotConversationDetail />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/projects',
        element: (
          <AdminRoute>
            <AdminProjects />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/clips',
        element: (
          <AdminRoute>
            <AdminClips />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/downloads',
        element: (
          <AdminRoute>
            <AdminDownloads />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/coupons',
        element: (
          <AdminRoute>
            <AdminCoupons />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/live-watches',
        element: (
          <AdminRoute>
            <AdminLiveWatches />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/live-watches/:id',
        element: (
          <AdminRoute>
            <AdminLiveWatchDetail />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/course-revenue-shares',
        element: (
          <AdminRoute>
            <AdminCourseRevenueShares />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/affiliate-earnings',
        element: (
          <AdminRoute>
            <AdminAffiliateEarnings />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/email-flows',
        element: <AdminRoute><AdminEmailFlows /></AdminRoute>,
      },
      {
        path: '/admin/email-flows/new',
        element: <AdminRoute><AdminEmailFlowDetail /></AdminRoute>,
      },
      {
        path: '/admin/email-flows/:id',
        element: <AdminRoute><AdminEmailFlowDetail /></AdminRoute>,
      },
      {
        path: '/admin/payouts',
        element: (
          <AdminRoute>
            <AdminPayouts />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/pix-verifications',
        element: (
          <AdminRoute>
            <AdminPixVerifications />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/contatos',
        element: (
          <AdminRoute>
            <AdminContatos />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/cpm-leads',
        element: (
          <AdminRoute>
            <AdminCpmLeads />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/challenges',
        element: (
          <AdminRoute>
            <AdminChallenges />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/challenges/:challengeId/participants',
        element: (
          <AdminRoute>
            <AdminChallengeParticipants />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/challenges/:challengeId/clips',
        element: (
          <AdminRoute>
            <AdminChallengeClips />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/challenges/:challengeId/participants/:participantId/audit',
        element: (
          <AdminRoute>
            <AdminChallengeAudit />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/challenges/:challengeId/rankings',
        element: (
          <AdminRoute>
            <AdminChallengeRankings />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/library',
        element: (
          <AdminRoute>
            <AdminLibrary />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/brand-kit-new',
        element: (
          <AdminRoute>
            <AdminBrandKitNew />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/template-suggestions',
        element: (
          <AdminRoute>
            <AdminTemplateSuggestions />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/community-curation',
        element: (
          <AdminRoute>
            <AdminCommunityCuration />
          </AdminRoute>
        ),
      },
      {
        path: '/motion-test',
        element: <AdminRoute><MotionTest /></AdminRoute>,
      },
      {
        path: '/admin/ai-usage',
        element: (
          <AdminRoute>
            <AdminAiUsage />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/campaigns',
        element: (
          <AdminRoute>
            <AdminCampaignsListPage />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/clipper-seasons',
        element: (
          <AdminRoute>
            <AdminClipperSeasons />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/channels',
        element: (
          <AdminRoute>
            <AdminChannels />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/campaigns/new',
        element: (
          <AdminRoute>
            <AdminCampaignEditPage />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/campaigns/:id',
        element: (
          <AdminRoute>
            <AdminCampaignEditPage />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/campaigns/:id/submissions',
        element: (
          <AdminRoute>
            <AdminCampaignSubmissionsPage />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/campaigns/:id/participants',
        element: (
          <AdminRoute>
            <AdminCampaignParticipantsPage />
          </AdminRoute>
        ),
      },
      {
        path: '/campaign-audit',
        element: (
          <ProtectedRoute>
            <CampaignAuditListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/campaign-audit/:id',
        element: (
          <ProtectedRoute>
            <AdminCampaignSubmissionsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/campaign-audit/:id/participants',
        element: (
          <ProtectedRoute>
            <AdminCampaignParticipantsPage />
          </ProtectedRoute>
        ),
      },
    ],
    errorElement: <NotFound />,
  },
]);

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

// Re-create router with Sentry instrumentation if DSN is configured
const instrumentedRouter = import.meta.env.VITE_SENTRY_DSN
  ? sentryCreateBrowserRouter(router.routes)
  : router;

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ApolloProvider client={client}>
      <Sentry.ErrorBoundary fallback={<NotFound />} showDialog>
        <RouterProvider router={instrumentedRouter} />
      </Sentry.ErrorBoundary>
    </ApolloProvider>
  </StrictMode>
);
